import { BookOpen, Compass, FileSearch, Globe, Lock, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { cn, dominionUrl } from "@/lib/utils";
import type { EntitlementKey } from "@/types";

interface Feature {
  key: EntitlementKey;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  /** Where the feature lives once unlocked; null while it isn't built yet. */
  to: string | null;
}

const FEATURES: Feature[] = [
  {
    key: "live_portfolio",
    title: "Live Portfolio",
    subtitle: "publish your verified work",
    icon: Globe,
    to: "/account",
  },
  {
    key: "cv_reviewer",
    title: "CV reviewer",
    subtitle: "feedback on your CV",
    icon: FileSearch,
    to: null,
  },
  {
    key: "research_handbook",
    title: "AI research handbook",
    subtitle: "the analyst's playbook",
    icon: BookOpen,
    to: null,
  },
  {
    key: "career_fit_quiz",
    title: "Career fit quiz",
    subtitle: "find your finance path",
    icon: Compass,
    to: null,
  },
];

const ROW = "flex items-center gap-[13px] rounded-xl border px-3.5 py-[11px]";

function FeatureBody({ feature, locked }: { feature: Feature; locked: boolean }) {
  const Icon = feature.icon;
  return (
    <>
      <Icon className="h-5 w-5 flex-none text-content-muted" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0">
        <span className="text-sm font-extrabold tracking-[-.01em]">{feature.title}</span>
        <span className="font-serif text-xs italic text-content-muted">{feature.subtitle}</span>
      </div>
      {locked && (
        <Lock
          className="h-4 w-4 flex-none text-content-muted"
          aria-hidden
          data-testid="lock-icon"
        />
      )}
    </>
  );
}

/** The Entitlement-gated features (KC-496), driven by the payload's `entitlements`.
 *
 * Without an Entitlement a feature is shown greyed and locked — never hidden —
 * and links to the Dominion programme page. With it, a built feature links to
 * where it lives; one not built yet stays hidden until it is. */
export function ProgrammeFeatures({ entitlements }: { entitlements: EntitlementKey[] }) {
  const granted = new Set(entitlements);
  const rows = FEATURES.filter((f) => !granted.has(f.key) || f.to !== null);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-[14px] border border-content-border bg-card px-7 py-6">
      <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">
        YOUR PROGRAMME
      </div>
      <div className="mt-4 flex flex-col gap-2.5">
        {rows.map((feature) =>
          granted.has(feature.key) && feature.to ? (
            <Link
              key={feature.key}
              to={feature.to}
              className={cn(ROW, "border-content-border hover:bg-gray-50")}
            >
              <FeatureBody feature={feature} locked={false} />
            </Link>
          ) : (
            <a
              key={feature.key}
              href={dominionUrl()}
              data-locked="true"
              aria-label={`${feature.title} (locked)`}
              title="Included in the full Dominion programme"
              className={cn(
                ROW,
                "border-content-border bg-gray-50 opacity-60 grayscale hover:opacity-80"
              )}
            >
              <FeatureBody feature={feature} locked />
            </a>
          )
        )}
      </div>
    </div>
  );
}
